import { useQuery } from '@tanstack/react-query';
import { HttpStatusCode, isAxiosError } from 'axios';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { INITIAL_DATA, QUERY_KEYS, SEARCH_KEYS } from '@src/constants';
import { QUIZZES_QUERY_RETRY_COUNT } from '@src/constants/quizzes.constants';
import { getQuizzesContent } from '@src/helpers/quizzes.helpers';
import { useQuizService } from '@src/hooks/useQuizService';

import { quizzesService } from '../services/quizzes.services';

export const useQueryQuizzes = () => {
  const { i18n } = useTranslation();
  const { variant: savedVariant, setQuizData } = useQuizService();

  const [searchParams, setSearchParams] = useSearchParams();

  const variantFromQuery = searchParams.get(SEARCH_KEYS.VARIANT);
  const variant = variantFromQuery ?? savedVariant ?? null;

  useEffect(() => {
    if (!variantFromQuery) return;

    if (variantFromQuery !== savedVariant) {
      setQuizData({ ...INITIAL_DATA, variant: variantFromQuery });
    }

    setSearchParams(
      (prev) => {
        prev.delete(SEARCH_KEYS.VARIANT);
        return prev;
      },
      { replace: true },
    );
  }, [variantFromQuery, savedVariant, setQuizData, setSearchParams]);

  const query = useQuery({
    queryKey: [QUERY_KEYS.quizzes, variant, i18n.language],
    queryFn: async () => {
      const response = await quizzesService.list({
        variant,
        locale: i18n.language,
      });

      const data = response.data.data;

      return { ...data, content: getQuizzesContent(data.content), variant };
    },
    enabled: !!variant,
    retry: (failureCount, error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      return (
        failureCount < QUIZZES_QUERY_RETRY_COUNT &&
        !(status && status < HttpStatusCode.InternalServerError)
      );
    },
  });

  useEffect(() => {
    if (query.isError && savedVariant) setQuizData(INITIAL_DATA);
  }, [query.isError, savedVariant, setQuizData]);

  return { variant, ...query };
};
